// AgriLinkInnovationHub.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBook,
  faRocket,
  faTools,
  faComments,
  faBoxOpen,
  faHandshake,
  faGavel,
  faUsers,
  faRobot,
  faCommentsDollar,
  faCheckCircle,
  faRupeeSign,
  faStar,
} from "@fortawesome/free-solid-svg-icons";

/**
 * AgriLinkInnovationHub.jsx
 * - 10 portlets for Innovation Hub (functional dummy implementations).
 * - Style consistent with your other hubs: green/yellow/gray theme, portlet pattern.
 */

const Portlet = ({ icon, title, desc, expanded, onToggle, children }) => {
  return (
    <div className="bg-gray-600 border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition flex flex-col items-center p-4">
      <button
        onClick={onToggle}
        className="py-4 px-6 flex flex-col items-center gap-3 rounded-xl bg-green-700 hover:bg-green-800 transition w-full"
        aria-expanded={expanded}
      >
        <div className="flex-shrink-0">
          <div className="w-12 h-12 rounded-lg bg-green-50 text-green-600 flex items-center justify-center text-xl">
            <FontAwesomeIcon icon={icon} />
          </div>
        </div>

        <div className="w-full">
          <div className="flex justify-between items-start gap-2">
            <div>
              <h3 className="text-lg font-semibold text-yellow-200">{title}</h3>
              <p className="text-sm text-white mt-1">{desc}</p>
            </div>
            <div className="px-4 py-2 rounded-lg text-xs bg-blue-500 text-white">{expanded ? "Collapse" : "Open"}</div>
          </div>
        </div>
      </button>

      {expanded && <div className="p-5 border-t border-gray-100 bg-green-50/40 w-full">{children}</div>}
    </div>
  );
};

/* -------------------- Subcomponents for portlets -------------------- */

/* 1. Knowledge Zone - search + short articles */
const KnowledgeZone = () => {
  const articles = [
    { id: 1, title: "Hydroponics 101", tag: "Basics", snippet: "Step-by-step hydroponics introduction for small farms." },
    { id: 2, title: "Drones in Crop Health", tag: "Drones", snippet: "How multispectral imaging helps early pest detection." },
    { id: 3, title: "Biofertilizers for Paddy", tag: "Inputs", snippet: "Field-tested biofertilizers that improve yields." },
  ];
  const [q, setQ] = useState("");
  const results = articles.filter((a) => a.title.toLowerCase().includes(q.toLowerCase()) || a.tag.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="mb-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search knowledge (e.g., hydroponics, drones)" className="w-full p-2 border rounded" />
      </div>

      <div className="space-y-2">
        {results.map((a) => (
          <div key={a.id} className="bg-white p-3 rounded border">
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-semibold">{a.title}</h4>
                <p className="text-sm text-gray-500">{a.tag}</p>
                <p className="text-sm text-gray-700 mt-1">{a.snippet}</p>
              </div>
              <button className="ml-3 text-sm text-white border border-green-200 px-3 py-1 rounded">Read</button>
            </div>
          </div>
        ))}
        {results.length === 0 && <div className="text-sm text-gray-500">No articles found.</div>}
      </div>
    </div>
  );
};

/* 2. Innovative Products (0-2 years) - marketplace */
const InnovativeProducts = () => {
  const [products] = useState([
    { id: 1, name: "Smart LED Rack v2", price: 45000, ageMonths: 10, rating: 4.5, desc: "Energy-efficient vertical LED grow rack." },
    { id: 2, name: "Auto Nutrient Dispenser", price: 15000, ageMonths: 6, rating: 4.2, desc: "Automates nutrient dosing by schedule." },
  ]);
  const [wish, setWish] = useState([]);
  const toggleWish = (p) => setWish((w) => (w.includes(p.id) ? w.filter((id) => id !== p.id) : [...w, p.id]));

  return (
    <div>
      <div className="grid sm:grid-cols-2 gap-3">
        {products.map((p) => (
          <div key={p.id} className="bg-white p-3 rounded border">
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-semibold">{p.name}</h4>
                <p className="text-sm text-gray-500">{p.desc}</p>
                <div className="mt-2 text-sm text-gray-600">
                  Launched {p.ageMonths} months ago • <span className="text-yellow-600"><FontAwesomeIcon icon={faStar} /> {p.rating}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-green-700 font-semibold"><FontAwesomeIcon icon={faRupeeSign} /> {p.price}</div>
                <button onClick={() => toggleWish(p)} className="w-32 mt-2 px-3 py-1 rounded border text-sm">{wish.includes(p.id) ? "Wishlisted" : "Wishlist"}</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* 3. Innovative Services - list and request service */
const InnovativeServices = () => {
  const services = [
    { id: 1, name: "Drone Spray Service", pricePerHa: 1500, desc: "Fast aerial spraying for large farms." },
    { id: 2, name: "Soil Lab On Wheels", pricePerSample: 300, desc: "Rapid nutrient & pH testing at farm." },
  ];
  const [requests, setRequests] = useState([]);
  const requestService = (s) => {
    const contact = prompt(`Enter contact for requesting ${s.name} (dummy):`);
    if (!contact) return;
    setRequests((r) => [...r, { id: Date.now(), service: s.name, contact }]);
    alert(`Service request sent for ${s.name} (dummy)`);
  };

  return (
    <div>
      <div className="space-y-2">
        {services.map((s) => (
          <div key={s.id} className="bg-white p-3 rounded border flex justify-between items-start">
            <div>
              <h4 className="font-semibold">{s.name}</h4>
              <p className="text-sm text-gray-500">{s.desc}</p>
            </div>
            <div className="text-right">
              <div className="text-green-700 font-semibold"><FontAwesomeIcon icon={faRupeeSign} /> {s.pricePerHa || s.pricePerSample}</div>
              <button onClick={() => requestService(s)} className="mt-2 bg-green-600 text-white px-3 py-1 rounded">Request</button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 bg-white p-3 rounded border">
        <h5 className="font-semibold">Requests (dummy)</h5>
        {requests.length === 0 ? <p className="text-sm text-gray-500">No requests yet.</p> : requests.map((r) => <div key={r.id} className="text-sm">{r.service} • {r.contact}</div>)}
      </div>
    </div>
  );
};

/* 4. Farmer Feedback & Field Trials (post + gallery) */
const FeedbackTrials = () => {
  const [trials, setTrials] = useState([
    { id: 1, farmer: "Rani", product: "Auto Nutrient Dispenser", summary: "Reduced manual dosing, small yield uptick", pics: [] },
  ]);
  const [form, setForm] = useState({ farmer: "", product: "", summary: "" });

  const submit = (e) => {
    e.preventDefault();
    setTrials((t) => [...t, { ...form, id: Date.now(), pics: [] }]);
    setForm({ farmer: "", product: "", summary: "" });
    alert("Feedback posted (dummy)");
  };

  return (
    <div>
      <form onSubmit={submit} className="bg-gray-600 p-3 rounded mb-3">
        <input required value={form.farmer} onChange={(e) => setForm({ ...form, farmer: e.target.value })} placeholder="Farmer name" className="w-full p-2 border rounded mb-2" />
        <input required value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} placeholder="Product tried" className="w-full p-2 border rounded mb-2" />
        <textarea required value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="Short summary" className="w-full p-2 border rounded mb-2 bg-gray-200 text-black" style={{height: "400px", width: "100%"}} />
        <button className="w-full bg-green-600 text-white py-2 rounded">Post Feedback</button>
      </form>

      <div className="space-y-2">
        {trials.map((t) => (
          <div key={t.id} className="bg-white p-3 rounded border">
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-semibold">{t.product} — <span className="text-sm text-gray-500">{t.farmer}</span></h4>
                <p className="text-sm text-gray-700 mt-1">{t.summary}</p>
              </div>
              <div className="text-xs text-gray-500">Photos: {t.pics.length}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* 5. Sample Ask & Delivery (request sample, track status) */
const SampleAsk = () => {
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState({ name: "", product: "", address: "" });

  const submit = (e) => {
    e.preventDefault();
    const id = Date.now();
    setRequests((r) => [...r, { ...form, id, status: "Requested" }]);
    setForm({ name: "", product: "", address: "" });
    alert("Sample requested (dummy)");
  };

  const progress = (id) => {
    setRequests((r) => r.map((req) => (req.id === id ? { ...req, status: req.status === "Delivered" ? "Delivered" : "Dispatched" } : req)));
  };

  return (
    <div>
      <form onSubmit={submit} className="bg-white p-3 rounded border mb-3">
        <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full p-2 border rounded mb-2" />
        <input required placeholder="Product (sample)" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} className="w-full p-2 border rounded mb-2" />
        <input required placeholder="Delivery address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full p-2 border rounded mb-2" />
        <button className="w-full bg-green-600 text-white py-2 rounded">Request Sample</button>
      </form>

      <div className="bg-white p-3 rounded border">
        <h5 className="font-semibold">Sample Requests</h5>
        {requests.length === 0 ? <p className="text-sm text-gray-500">No sample requests yet.</p> : requests.map((r) => (
          <div key={r.id} className="flex justify-between items-center py-2 border-b text-sm">
            <div>{r.product} • {r.name}</div>
            <div className="flex items-center gap-2">
              <div className="text-sm text-gray-600">{r.status}</div>
              <button onClick={() => progress(r.id)} className="text-sm text-blue-600">Advance</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* 6. Innovation Accelerator - pitch submission + investors list */
const Accelerator = () => {
  const [submissions, setSubmissions] = useState([]);
  const [form, setForm] = useState({ founder: "", idea: "", contact: "" });
  const investors = [
    { id: 1, name: "AgriImpact VC", focus: "Early-stage agri-tech" },
    { id: 2, name: "GreenSeed Fund", focus: "Sustainability & startups" },
  ];

  const submit = (e) => {
    e.preventDefault();
    setSubmissions((s) => [...s, { ...form, id: Date.now() }]);
    setForm({ founder: "", idea: "", contact: "" });
    alert("Pitch submitted (dummy)");
  };

  return (
    <div>
      <form onSubmit={submit} className="bg-gray-600 p-3 rounded mb-3">
        <input required placeholder="Founder / Team" value={form.founder} onChange={(e) => setForm({ ...form, founder: e.target.value })} className="w-full p-2 border rounded mb-2" />
        <input required placeholder="Idea / Product name" value={form.idea} onChange={(e) => setForm({ ...form, idea: e.target.value })} className="w-full p-2 border rounded mb-2" />
        <input required placeholder="Contact email/phone" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="w-full p-2 border rounded mb-2" />
        <button className="w-full bg-green-600 text-white py-2 rounded">Submit Pitch</button>
      </form>

      <div className="bg-white p-3 rounded border mb-3">
        <h5 className="font-semibold">Investor Partners</h5>
        {investors.map((inv) => (
          <div key={inv.id} className="flex justify-between text-sm py-1 border-b">
            <div>{inv.name}</div>
            <div className="text-gray-500">{inv.focus}</div>
          </div>
        ))}
      </div>

      <div className="bg-white p-3 rounded border">
        <h5 className="font-semibold">Submissions (dummy)</h5>
        {submissions.length === 0 ? <p className="text-sm text-gray-500">No submissions yet.</p> : submissions.map((s) => <div key={s.id} className="text-sm border-b py-1">{s.founder} • {s.idea}</div>)}
      </div>
    </div>
  );
};

/* 7. Policy & Schemes corner (list) */
const PolicyCorner = () => {
  const policies = [
    { id: 1, title: "PM-Kisan Subsidy update", snippet: "New guidelines for agri-tech adoption." },
    { id: 2, title: "Export Support Scheme", snippet: "Assistance for small exporters adopting traceability." },
  ];
  return (
    <div>
      {policies.map((p) => (
        <div key={p.id} className="bg-white p-3 rounded border mb-2">
          <h4 className="font-semibold">{p.title}</h4>
          <p className="text-sm text-gray-500">{p.snippet}</p>
          <div className="mt-2 text-right">
            <button className="text-sm text-white">Read</button>
          </div>
        </div>
      ))}
    </div>
  );
};

/* 8. Community & Collaboration (challenges, hackathons) */
const CommunityCollab = () => {
  const [challenges] = useState([
    { id: 1, title: "Low-cost IoT for small plots", prize: 50000, deadline: "2025-11-30" },
    { id: 2, title: "Soil-less herbs for apartments", prize: 30000, deadline: "2025-10-10" },
  ]);
  return (
    <div>
      {challenges.map((c) => (
        <div key={c.id} className="bg-white p-3 rounded border mb-2 flex justify-between items-start">
          <div>
            <h4 className="font-semibold">{c.title}</h4>
            <p className="text-sm text-gray-500">Deadline: {c.deadline}</p>
          </div>
          <div className="text-right">
            <div className="text-green-700 font-semibold"><FontAwesomeIcon icon={faRupeeSign} /> {c.prize}</div>
            <button className="w-40 mt-2 bg-green-600 text-white px-3 py-1 rounded">Participate</button>
          </div>
        </div>
      ))}
    </div>
  );
};

/* 9. Smart Tools (simple AI Advisor simulation) */
const SmartTools = () => {
  const [q, setQ] = useState("");
  const [resp, setResp] = useState("");
  const ask = (e) => {
    e.preventDefault();
    // simple heuristic responses (dummy)
    const t = q.toLowerCase();
    let r = "Try our Knowledge Zone or request a sample.";
    if (t.includes("biofert")) r = "Suggested: 'BioBoost' (trial 250g). Use during early vegetative stage.";
    if (t.includes("drone")) r = "Drone spray recommended for >2 ha. Consider 'DroneX' service.";
    if (t.includes("hydroponic")) r = "Start with lettuce or herbs; low-cost kit ROI ~6 months.";
    setResp(r);
  };

  return (
    <div>
      <form onSubmit={ask} className="flex gap-2 mb-3">
        <div className="flex flex-col">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask (e.g., best biofertilizer for paddy)" className="flex-1 w-full p-2 border rounded" />
          <button type="submit" className="bg-green-600 text-white px-3 py-2 rounded mt-2">Ask</button>
        </div>
      </form>
      {resp && <div className="bg-white p-3 rounded border text-sm">{resp}</div>}
    </div>
  );
};

/* 10. Discussion Circles (simple threads) */
const DiscussionCircles = () => {
  const [threads, setThreads] = useState([
    { id: 1, title: "Best low-cost sensors", posts: 4 },
    { id: 2, title: "Organic pest control ideas", posts: 9 },
  ]);
  const [title, setTitle] = useState("");
  const create = (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setThreads((t) => [{ id: Date.now(), title, posts: 0 }, ...t]);
    setTitle("");
  };

  return (
    <div>
      <form onSubmit={create} className="mb-3">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Start a discussion (thread)" className="w-full p-2 border rounded" />
      </form>
      <div className="space-y-2">
        {threads.map((th) => (
          <div key={th.id} className="bg-white p-3 rounded border flex justify-between">
            <div>
              <h4 className="font-semibold">{th.title}</h4>
              <p className="text-sm text-gray-500">{th.posts} posts</p>
            </div>
            <div className="flex items-center gap-2">
              <button className="w-24 text-sm bg-green-500 text-white">Open</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ------------------- Innovation Hub Container ------------------- */

export default function AgriLinkInnovationHub() {
  const [active, setActive] = useState(null);

  const useCases = [
    { id: "knowledge", title: "Knowledge Zone", desc: "Wiki, explainers & curated research", icon: faBook, component: <KnowledgeZone /> },
    { id: "products", title: "Innovative Products", desc: "Launchpad (0–2 years) & reviews", icon: faRocket, component: <InnovativeProducts /> },
    { id: "services", title: "Innovative Services", desc: "DaaS, soil lab, advisory services", icon: faTools, component: <InnovativeServices /> },
    { id: "feedback", title: "Farmer Feedback & Trials", desc: "Field trial reports & ratings", icon: faComments, component: <FeedbackTrials /> },
    { id: "samples", title: "Sample Ask & Delivery", desc: "Request samples & track status", icon: faBoxOpen, component: <SampleAsk /> },
    { id: "accelerator", title: "Innovation Accelerator", desc: "Pitch, investors & funding", icon: faHandshake, component: <Accelerator /> },
    { id: "policy", title: "Policy & Schemes", desc: "Subsidies, regs & certification", icon: faGavel, component: <PolicyCorner /> },
    { id: "community", title: "Community & Challenges", desc: "Hackathons & collaboration", icon: faUsers, component: <CommunityCollab /> },
    { id: "smart", title: "Smart Tools (AI Advisor)", desc: "Ask an AI advisor for quick suggestions", icon: faRobot, component: <SmartTools /> },
    { id: "circles", title: "Discussion Circles", desc: "Crop-specific discussion threads", icon: faCommentsDollar, component: <DiscussionCircles /> },
  ];

  return (
    <div>
      <section className="min-h-screen bg-gradient-to-r from-green-700 to-green-800 py-12 px-4 md:px-10 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <header className="text-center mb-8">
            <h1 className="text-lg md:text-xl font-bold text-yellow-200 flex items-center justify-center gap-3">
              <span className="text-xl bg-green-100 text-yellow-200 rounded-full w-6 h-6 flex items-center justify-center">💡</span>
              MSME/Rural/Agri Innovation Hub
            </h1>
            <p className="mt-3 text-sm text-white max-w-3xl mx-auto">
              Discover and pilot breakthrough MSME, Rural, Agri-innovations, connect with startups, request samples, and collaborate with investors and farmers.
            </p>
          </header>
{/* Innovative Product Launched Section */}
<div className="bg-gray-600 border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition p-6 mb-8">
  <h2 className="text-xl font-bold text-yellow-200 mb-6 text-center">
    🚀 Innovative Product Launched
  </h2>
  
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
    {/* Product Card 1 */}
    <div className="bg-white rounded-xl shadow p-5 flex flex-col justify-between">
      <div>
        <img
          src="https://via.placeholder.com/300x180.png?text=Smart+Irrigation+Kit"
          alt="Smart Irrigation Kit"
          className="rounded-lg mb-4"
        />
        <h3 className="text-lg font-semibold text-green-700">Smart Irrigation Kit</h3>
        <p className="text-sm text-gray-600 mt-2">
          IoT-based irrigation control system saving 30% water for smallholder farmers.
        </p>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg">
          Enquiry
        </button>
        <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg">
          Order
        </button>
        <button className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white py-2 rounded-lg">
          Contact
        </button>
      </div>
    </div>

    {/* Product Card 2 */}
    <div className="bg-white rounded-xl shadow p-5 flex flex-col justify-between">
      <div>
        <img
          src="https://via.placeholder.com/300x180.png?text=Drone+Crop+Scanner"
          alt="Drone Crop Scanner"
          className="rounded-lg mb-4"
        />
        <h3 className="text-lg font-semibold text-green-700">Drone Crop Scanner</h3>
        <p className="text-sm text-gray-600 mt-2">
          AI-powered drone for detecting crop stress, disease, and nutrient deficiencies.
        </p>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg">
          Enquiry
        </button>
        <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg">
          Order
        </button>
        <button className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white py-2 rounded-lg">
          Contact
        </button>
      </div>
    </div>

    {/* Product Card 3 */}
    <div className="bg-white rounded-xl shadow p-5 flex flex-col justify-between">
      <div>
        <img
          src="https://via.placeholder.com/300x180.png?text=Organic+Bio-Fertilizer"
          alt="Organic Bio-Fertilizer"
          className="rounded-lg mb-4"
        />
        <h3 className="text-lg font-semibold text-green-700">Organic Bio-Fertilizer</h3>
        <p className="text-sm text-gray-600 mt-2">
          Next-gen organic fertilizer improving soil health and boosting crop yields.
        </p>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg">
          Enquiry
        </button>
        <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg">
          Order
        </button>
        <button className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white py-2 rounded-lg">
          Contact
        </button>
        </div>
    </div>
    </div>
    </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {useCases.map((u) => (
            <Portlet key={u.id} icon={u.icon} title={u.title} desc={u.desc} expanded={active === u.id} onToggle={() => setActive((s) => (s === u.id ? null : u.id))}>
            {u.component}
            <div className="mt-4 text-xs text-yellow-200">Tip: Click to expand — all content is dummy.</div>
            </Portlet>
        ))}
        </div>
    </div>
    </section>

      {/* Footer */}
      <footer className="bg-green-700 text-white py-10 px-4 sm:px-6 md:px-20">
        <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left">
          <p className="text-sm">&copy; 2025 Think5Whys Global DeepTech Solutions. All rights reserved.</p>
          <div className="flex space-x-6 mt-4 md:mt-0 text-sm">
            <Link to="/about-us" className="text-white hover:text-green-400 transition">About</Link>
            <Link to="/privacy-policy" className="text-white hover:text-green-400 transition">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-white hover:text-green-400 transition">Terms of Use</Link>
            <Link to="/contact-us" className="text-white hover:text-green-400 transition">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
